import type { ClientErrorReport } from '@atlas-control/shared';
import { ApiError } from './api';

type Kind = ClientErrorReport['kind'];

const recent = new Map<string, number>();
let sentThisMinute = 0;
let minuteStart = Date.now();

/**
 * Sends a browser-side error to `POST /api/client-errors`, where it is stored in
 * `error_logs` with `source = web`.
 *
 * `ApiError`s are not reported: the server recorded them when it answered.
 * Identical errors within 10 s are sent once, and at most 20 reports leave the
 * page per minute, so a render loop cannot flood the table.
 */
export function reportError(
  kind: Kind,
  error: unknown,
  extra: Partial<ClientErrorReport> = {},
): void {
  if (error instanceof ApiError) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const key = `${kind}|${err.message}`;
  const now = Date.now();
  if ((recent.get(key) ?? 0) > now - 10_000) return;
  recent.set(key, now);

  if (now - minuteStart > 60_000) {
    minuteStart = now;
    sentThisMinute = 0;
  }
  if (++sentThisMinute > 20) return;

  const report: ClientErrorReport = {
    kind,
    message: err.message.slice(0, 2000) || 'Unknown error',
    stack: err.stack?.slice(0, 10_000),
    url: window.location.href.slice(0, 2000),
    route: window.location.pathname.slice(0, 500),
    appVersion: import.meta.env.VITE_APP_VERSION,
    ...extra,
  };

  // keepalive lets the report leave even while the page is unloading.
  void fetch('/api/client-errors', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(report),
  }).catch(() => {
    // Nothing more can be done from the browser; never throw from the reporter.
  });
}

/** Global handlers for errors that escape React and the query layer. */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    reportError('window_error', event.error ?? new Error(event.message));
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError('unhandled_rejection', event.reason);
  });
}
